<template>
  <Form method="post" class="py-14" @submit="handleSubmit">
    <h1 class="font-bold text-center text-3xl">
      {{
        t(isPasswordLoginDisabled ? 'auth.sign-in' : 'auth.sign-in-or-request')
      }}
    </h1>
    <p class="text-center font-semibold">
      {{ t('auth.to-continue') }}
    </p>

    <TextInput
      v-if="!isPasswordLoginDisabled"
      v-model="email"
      :label="t('auth.email')"
      :placeholder="t('auth.email')"
      :rules="[isRequired, isEmail]"
      data-testid="login-email"
      @update:model-value="errorMessage = ''"
    />

    <Alert v-if="errorMessage" class="mt-4" :type="AlertType.ERROR" icon>
      {{ errorMessage }}
    </Alert>

    <Button
      v-if="!isPasswordLoginDisabled"
      :label="t('common.next')"
      class="w-full mt-10"
      type="submit"
      data-testid="login-next"
    />
    <ExternalAuthProviders
      @error="handleSocialLoginError"
      @link-accounts="handleSocialLoginAttempt"
    />
    <div
      v-if="
        organization &&
        organization.hasLoginScreenAdditionalMessage &&
        organization.loginScreenAdditionalMessage
      "
      class="text-xs text-center mt-8"
      v-html="organization.loginScreenAdditionalMessage"
    />
  </Form>
</template>

<script setup lang="ts">
import {
  ref,
  useOrganizationDomain,
  useNuxtApp,
  storeToRefs,
  useI18n,
  useRoute,
} from '#imports';
import { Form } from 'vee-validate';
import { TextInput } from '~/components/inputs/text-input';
import { Button, Alert, AlertType } from '@exfluential/ui';
import { isEmailI18n, isRequiredI18n } from '~/utils/validators';
import { useLoader } from '~/composables/use-loader';
import { useVModel } from '@vueuse/core';
import {
  apiCheckEmail,
  EmailStatus,
  SelfRegisterType,
  apiResendInvitation,
  apiActivateUser,
} from '~/api/auth';
import { ExternalAuthProviders } from '~/pages/auth/_components/external-auth-providers';
import { useOrganizationStore } from '~/store/organization';
import { usePostHog } from '~/composables/usePostHog';
import { getFirstQueryParam } from '~/utils/common';
import { captureHandledError } from '~/constants/sentry';

type Props = {
  modelValue: string;
};
const props = defineProps<Props>();

const { organization } = storeToRefs(useOrganizationStore());

const { t } = useI18n();

const isRequired = isRequiredI18n(t);
const isEmail = isEmailI18n(t);

const { query } = useRoute();
const invitationCode = getFirstQueryParam(query.code);
const errorMessage = ref('');

const isPasswordLoginDisabled =
  organization?.value?.externalIdentityProviderData
    ?.passwordRegistrationDisabled || false;
const emit = defineEmits<{
  (e: 'change-type', value: SelfRegisterType): void;
  (e: 'update:modelValue', value: Props['modelValue']): void;
}>();

const email = useVModel(props, 'modelValue', emit);

const emailStatusSelfRegisterMap: Record<EmailStatus, SelfRegisterType> = {
  [EmailStatus.UNKNOWN]: SelfRegisterType.REQUEST_INVITE,
  [EmailStatus.NEW]: SelfRegisterType.REGISTER_YOURSELF,
  [EmailStatus.PENDING]: SelfRegisterType.RESEND_WELCOME_INVITE,
  [EmailStatus.REGISTERED]: SelfRegisterType.ENTER_PASSWORD,
};

const extractErrorDetails = (error: unknown) => {
  const details = {
    message: 'Unknown error',
    statusCode: undefined as number | undefined,
    fullError: undefined as string | undefined,
  };

  try {
    if (error && typeof error === 'object') {
      if ('data' in error && error.data && typeof error.data === 'object') {
        if ('message' in error.data && typeof error.data.message === 'string') {
          details.message = error.data.message;
        }
        if (
          'statusCode' in error.data &&
          typeof error.data.statusCode === 'number'
        ) {
          details.statusCode = error.data.statusCode;
        }
      }

      if ('message' in error && typeof error.message === 'string') {
        details.message = error.message;
      }

      if ('status' in error && typeof error.status === 'number') {
        details.statusCode = error.status;
      }

      if (
        'response' in error &&
        error.response &&
        typeof error.response === 'object'
      ) {
        if (
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data &&
          typeof error.response.data.message === 'string'
        ) {
          details.message = error.response.data.message;
        }
        if (
          'status' in error.response &&
          typeof error.response.status === 'number'
        ) {
          details.statusCode = error.response.status;
        }
      }

      details.fullError = JSON.stringify(
        error,
        Object.getOwnPropertyNames(error),
      );
    } else if (typeof error === 'string') {
      details.message = error;
    }
  } catch {
    details.message = String(error);
  }

  return details;
};

const setErrorMessageFromError = (error: unknown) => {
  const errorDetails = extractErrorDetails(error);
  const isSsoEnabled = Boolean(
    organization.value?.externalIdentityProviderData,
  );

  if (isSsoEnabled) {
    captureHandledError(error, { feature: 'sso' });
  }

  errorMessage.value = isSsoEnabled
    ? t('auth.sso-error')
    : errorDetails.message;
};

const { capture } = usePostHog();

const organizationDomain = useOrganizationDomain();

const handleSubmit = async () => {
  await useLoader({
    action: async () => {
      capture('login_attempt', {
        email_domain: email.value.split('@')[1],
        has_invitation_code: Boolean(invitationCode),
        organization_domain: organizationDomain,
        has_sso_enabled: Boolean(
          organization.value?.externalIdentityProviderData,
        ),
      });

      if (invitationCode) {
        await apiActivateUser({ invitationCode });
      }
      const { emailStatus } = await apiCheckEmail({ email: email.value });
      if (emailStatus === EmailStatus.PENDING) {
        await apiResendInvitation({ email: email.value });
      }
      emit('change-type', emailStatusSelfRegisterMap[emailStatus]);

      capture('login_email_check', {
        email_status: emailStatus,
        next_step: emailStatusSelfRegisterMap[emailStatus],
        organization_domain: organizationDomain,
      });
    },
    onError(err: unknown) {
      setErrorMessageFromError(err);
      const errorDetails = extractErrorDetails(err);

      capture('login_error', {
        error_message: errorDetails.message,
        status_code: errorDetails.statusCode,
        error_type: 'email_check',
        organization_domain: organizationDomain,
        has_sso_enabled: Boolean(
          organization.value?.externalIdentityProviderData,
        ),
        full_error: errorDetails.fullError,
      });
    },
  });
};

const isMarketmylaserDomain = organizationDomain === 'marketmylaser.com';

const handleSocialLoginError = (error: unknown) => {
  setErrorMessageFromError(error);
  const errorDetails = extractErrorDetails(error);

  capture('social_login_error', {
    error_message: errorDetails.message,
    status_code: errorDetails.statusCode,
    error_type: 'social_auth',
    organization_domain: organizationDomain,
    has_sso_enabled: Boolean(organization.value?.externalIdentityProviderData),
    full_error: errorDetails.fullError,
  });
};

const handleSocialLoginAttempt = (emailFromProvider: string) => {
  if (emailFromProvider) {
    email.value = emailFromProvider;
  }
  emit('change-type', SelfRegisterType.ENTER_PASSWORD);
  capture('social_login_attempt', {
    provider: emailFromProvider ? 'sso' : 'unknown',
    organization_domain: organizationDomain,
    has_sso_enabled: Boolean(organization.value?.externalIdentityProviderData),
  });
};
</script>
